import { useEffect, useState } from 'react';

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { AppIcon } from '@/components/ui/app-icon';
import { iconChevronDown } from '@/lib/icons';
import { cn } from '@/lib/utils';

import {
  importCountPresets,
  parseImportCount,
} from '@/components/agent-chat/import-count.util';

/**
 * How many leads to import: type any number up to `max`, or click the field
 * for the presets. `onChange` only ever gets a whole number from 1 to `max`;
 * a field left empty goes back to `value`.
 */
export function ImportCountField({
  value,
  max,
  onChange,
  disabled,
  className,
}: {
  value: number;
  max: number;
  onChange: (count: number) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(String(value));

  // A preset or a new request moves the field; typing that matches stays put.
  useEffect(() => {
    setDraft((current) =>
      current === '' || Number(current) === value ? current : String(value)
    );
  }, [value]);

  const presets = importCountPresets(max);
  // Plain digits while typing, so the caret stays where the user put it;
  // grouped once they leave the field.
  const display = focused || !draft ? draft : Number(draft).toLocaleString();

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          className={cn(
            'flex h-[32px] w-[112px] items-center rounded-[8px] border border-border bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1',
            disabled && 'opacity-50',
            className
          )}
          data-import-count-field
        >
          <input
            type='text'
            inputMode='numeric'
            aria-label='Leads to import'
            value={display}
            disabled={disabled}
            onFocus={() => {
              setFocused(true);
              setOpen(true);
            }}
            onClick={() => setOpen(true)}
            onChange={(event) => {
              const next = parseImportCount(event.target.value, max);
              setDraft(next === null ? '' : String(next));
              if (next !== null) onChange(next);
            }}
            onBlur={() => {
              setFocused(false);
              if (!draft) setDraft(String(value));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === 'Escape') {
                setOpen(false);
              }
            }}
            className='h-full min-w-0 flex-1 bg-transparent pl-[10px] text-[12px] font-medium text-foreground tabular-nums outline-hidden'
          />
          <button
            type='button'
            tabIndex={-1}
            aria-label='Show lead count presets'
            disabled={disabled}
            onClick={() => setOpen((current) => !current)}
            className='flex h-full shrink-0 items-center px-[8px] text-muted-foreground hover:text-foreground'
          >
            <AppIcon icon={iconChevronDown} className='h-[14px] w-[14px]' />
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align='start'
        sideOffset={4}
        className='w-[112px] p-[4px]'
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-import-count-field]')) {
            event.preventDefault();
          }
        }}
      >
        <div role='listbox' aria-label='Lead count presets'>
          {presets.map((count) => (
            <button
              key={count}
              type='button'
              role='option'
              aria-selected={value === count}
              onClick={() => {
                setDraft(String(count));
                onChange(count);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center rounded-[6px] px-[8px] py-[5px] text-left text-[12px] text-foreground tabular-nums hover:bg-muted/60',
                value === count && 'bg-muted font-medium'
              )}
            >
              {count.toLocaleString()}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
